<script setup lang="ts">
// The onboarding takeover's host — v3 `DefaultLayout.vue:45` mounting `OnboardingModal` over the
// app. Mounted once at the app root; it opens the wizard by itself when onboarding is mandatory
// (`useOnboardingGate`), reopens a run an OAuth round trip interrupted, and otherwise waits for an
// explicit `start` (the workspace menu's "New workspace").
//
// A mandatory run cannot be dismissed: no Escape, no outside press, no close control — the only
// way out is finishing it. An optional run closes from the logo, as v3's did.
import { defineAsyncComponent, watch } from 'vue'

import { useOnboardingGate } from '../../composables/useOnboardingGate'
import { registerGlobalSurfaceMessages } from '../../i18n'
import { useOnboardingStore } from '../../stores/onboardingStore'

import { registerIntegrationsMessages } from '@/features/integrations'
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  i18n,
  useI18n,
  type SupportedLocale,
} from '@/shared/i18n'
import { Takeover } from '@/shared/ui'

// Lazy: the wizard and its four steps load only when a run opens, never with the shell.
const OnboardingWizard = defineAsyncComponent(() => import('./OnboardingWizard.vue'))

const { t } = useI18n()
const store = useOnboardingStore()
const gate = useOnboardingGate()

function activeLocale(): SupportedLocale {
  const locale = i18n.global.locale.value
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale)
    ? (locale as SupportedLocale)
    : DEFAULT_LOCALE
}

// Steps 3 and 4 host module 12's connector facade, whose copy lives in its own bundle — what the
// retired route's `beforeEnter` loaded.
watch(
  () => store.open,
  (open) => {
    if (!open) return
    const locale = activeLocale()
    void Promise.all([registerGlobalSurfaceMessages(locale), registerIntegrationsMessages(locale)])
  },
  { immediate: true },
)

// Back from an OAuth consent screen mid-run: pick up where it left. Before the gate below, so a
// mandatory run resumes at its step instead of restarting at step 1.
if (!store.open) store.resume()

watch(
  gate.required,
  (required) => {
    if (!required) return
    if (store.open) {
      store.require()
      return
    }
    // Only a Shopify signup already has its workspace, so only it may skip step 1.
    store.start({ mode: 'required', step: gate.shopify.value ? 'settings' : 'who-brand' })
  },
  { immediate: true },
)
</script>

<template>
  <Takeover
    :model-value="store.open"
    :label="t('globalSurface.onboarding.title')"
    data-testid="onboarding-takeover"
  >
    <OnboardingWizard />
  </Takeover>
</template>
